import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, adminAxios } from "../AuthContext";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import ImmobileEditor from "./ImmobileEditor";
import BorgoSettingsPanel from "./BorgoSettingsPanel";
import TenantSettingsPanel from "./TenantSettingsPanel";
import LlmUsagePanel from "./LlmUsagePanel";
import { Home, Calendar, Landmark, LogOut, Plus, FileSpreadsheet, FileText, Search, Edit, Trash2, Loader2, Image as ImageIcon, MapPin, Eye, EyeOff, Download, Settings, Paintbrush, Cpu } from "lucide-react";
import { Input, Textarea, Select, Toggle, FileUploader } from "./FormFields";

const ADMIN_TABS = [
    { id: "personalizza", label: "Personalizza Borgo", icon: Paintbrush },
    { id: "immobili", label: "Immobili", icon: Home },
    { id: "eventi", label: "Eventi", icon: Calendar },
    { id: "attrazioni", label: "Attrattori POI", icon: Landmark },
    { id: "borgo", label: "Digital Twin Borgo", icon: Settings },
    { id: "llm-usage", label: "Consumi AI", icon: Cpu },
];

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    // Parse ?tab= from either standard search OR from inside the hash (react-router v6 HashRouter)
    // Also honour sessionStorage 'tada_pending_admin_tab' (set by Super Admin's "Personalizza" button
    // before impersonation, which can survive the router's Navigate race).
    const readTabFromLocation = () => {
        try {
            const qs = new URLSearchParams(location.search || "");
            const t = qs.get("tab");
            if (t) return t;
            const hash = window.location.hash || "";
            const qIndex = hash.indexOf("?");
            if (qIndex >= 0) {
                const raw = new URLSearchParams(hash.substring(qIndex + 1));
                const t2 = raw.get("tab");
                if (t2) return t2;
            }
            const pending = sessionStorage.getItem("tada_pending_admin_tab");
            if (pending) {
                sessionStorage.removeItem("tada_pending_admin_tab");
                return pending;
            }
        } catch (_e) { /* ignore */ }
        return null;
    };
    const [tab, setTab] = useState(() => readTabFromLocation() || "immobili");
    const [tenantName, setTenantName] = useState("");

    // Sync tab when the URL query changes (e.g. Super Admin's "Personalizza" button)
    useEffect(() => {
        const t = readTabFromLocation();
        if (t && t !== tab) setTab(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, location.hash]);

    useEffect(() => {
        const slug = user?.tenant_slug;
        if (!slug) { setTenantName(""); return; }
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tenants/public/${slug}`)
            .then(r => setTenantName(r.data?.name || ""))
            .catch(() => setTenantName(""));
    }, [user?.tenant_slug]);

    if (loading) return <div className="pt-32 min-h-screen text-center text-stone-500">Caricamento…</div>;
    if (!user) return <Navigate to="/login" replace />;

    const dashboardTitle = tenantName
        ? `Dashboard ${tenantName}`
        : (user.role === "super_admin" ? "Dashboard Super Admin" : "Dashboard Admin");

    return (
        <div className="pt-24 min-h-screen bg-stone-100" data-testid="admin-dashboard">
            <div className="bg-white border-b border-stone-200 sticky top-20 z-30">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-blue-700">Amministratore</div>
                        <div className="font-serif text-2xl font-semibold text-stone-900" data-testid="admin-dashboard-title">{dashboardTitle}</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-stone-500 hidden md:inline">Ciao, <b className="text-stone-800">{user.username}</b></span>
                        <button onClick={() => { logout(); nav("/"); }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-50 font-semibold text-sm" data-testid="admin-logout">
                            <LogOut size={14} /> Esci
                        </button>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-1 border-t border-stone-100">
                    {ADMIN_TABS.map((t) => {
                        const Icon = t.icon;
                        const on = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                                    on ? "border-blue-600 text-blue-700" : "border-transparent text-stone-600 hover:text-blue-700"
                                }`}
                                data-testid={`admin-tab-${t.id}`}
                            >
                                <Icon size={15} />
                                {t.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
                {tab === "personalizza" && <TenantSettingsPanel />}
                {tab === "immobili" && <ImmobiliPanel />}
                {tab === "eventi" && <EventiPanel />}
                {tab === "attrazioni" && <AttrazioniPanel />}
                {tab === "borgo" && <BorgoSettingsPanel />}
                {tab === "llm-usage" && <LlmUsagePanel />}
            </div>
        </div>
    );
}

// ================= IMMOBILI PANEL =================
const ImmobiliPanel = () => {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ totale: 0, pubblicati: 0, da_ristrutturare: 0, liberi: 0 });
    const [q, setQ] = useState("");
    const [tipologia, setTipologia] = useState("");
    const [stato, setStato] = useState("");
    const [editor, setEditor] = useState({ open: false, item: null });
    const [busy, setBusy] = useState(true);

    const load = async () => {
        setBusy(true);
        try {
            const client = adminAxios();
            const [it, st] = await Promise.all([
                client.get("/immobili/all"),
                client.get("/immobili/stats"),
            ]);
            setItems(it.data);
            setStats(st.data);
        } finally { setBusy(false); }
    };

    useEffect(() => { load(); }, []);

    const del = async (id) => {
        if (!confirm("Confermi eliminazione immobile?")) return;
        await adminAxios().delete(`/immobili/${id}`);
        load();
    };

    const filtered = items.filter((it) => {
        const denom = (it.anagrafica?.denominazione || "").toLowerCase();
        const cod = (it.codice || "").toLowerCase();
        const qq = q.toLowerCase();
        if (qq && !denom.includes(qq) && !cod.includes(qq)) return false;
        if (tipologia && it.classificazione?.tipologia !== tipologia) return false;
        if (stato && it.stato_conservazione?.stato !== stato) return false;
        return true;
    });

    const downloadExcel = async () => {
        const token = localStorage.getItem("tada_admin_token");
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/immobili/export/excel`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "immobili.xlsx"; a.click();
        URL.revokeObjectURL(url);
    };

    const downloadPdf = async () => {
        const token = localStorage.getItem("tada_admin_token");
        const params = new URLSearchParams();
        if (q) params.set("q", q);
        if (tipologia) params.set("tipologia", tipologia);
        if (stato) params.set("stato", stato);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/immobili/export/pdf${params.toString() ? "?" + params.toString() : ""}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) { window.alert("Errore generazione PDF"); return; }
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        window.open(objectUrl, "_blank");
        setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
    };

    return (
        <div data-testid="admin-immobili-panel">
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-6">
                <div className="flex-1 flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-200">
                    <Search size={16} className="text-stone-500" />
                    <input type="text" placeholder="Cerca immobile..." value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 bg-transparent outline-none text-sm" data-testid="adm-imm-search" />
                </div>
                <select value={tipologia} onChange={(e) => setTipologia(e.target.value)} className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm" data-testid="adm-imm-tipologia">
                    <option value="">Tutte le tipologie</option>
                    {["Casa Singola", "Appartamento", "Rustico", "Commerciale", "Terreno Agricolo", "Terreno Edificabile"].map(t => <option key={t}>{t}</option>)}
                </select>
                <select value={stato} onChange={(e) => setStato(e.target.value)} className="px-4 py-2 rounded-full bg-white border border-stone-200 text-sm" data-testid="adm-imm-stato">
                    <option value="">Tutti gli stati</option>
                    {["Disponibile", "In Trattativa", "Venduto", "Riservato"].map(s => <option key={s}>{s}</option>)}
                </select>
                <button onClick={downloadExcel} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm" data-testid="adm-imm-excel">
                    <FileSpreadsheet size={14} /> Excel
                </button>
                <button onClick={downloadPdf} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-800 hover:bg-stone-900 text-white font-semibold text-sm" data-testid="adm-imm-pdf">
                    <FileText size={14} /> PDF
                </button>
                <button onClick={() => setEditor({ open: true, item: null })} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm" data-testid="adm-imm-new">
                    <Plus size={14} /> Nuovo Immobile
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                    { label: "Totale Immobili", value: stats.totale, color: "text-blue-700" },
                    { label: "Pubblicati", value: stats.pubblicati, color: "text-emerald-700" },
                    { label: "Da Ristrutturare", value: stats.da_ristrutturare, color: "text-amber-700" },
                    { label: "Liberi", value: stats.liberi, color: "text-green-700" },
                ].map((s) => (
                    <div key={s.label} className="bg-white rounded-2xl border border-stone-200 p-4" data-testid={`stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                        <div className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1">{s.label}</div>
                        <div className={`font-serif text-3xl font-semibold ${s.color}`}>{s.value}</div>
                    </div>
                ))}
            </div>

            {busy && <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-blue-600" /></div>}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((it) => {
                    const cover = it.marketing?.foto_copertina || it.marketing?.galleria?.[0] || "";
                    return (
                        <div key={it.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden card-hover" data-testid={`adm-imm-card-${it.codice}`}>
                            <div className="aspect-[16/10] bg-stone-100 relative">
                                {cover ? (
                                    <img src={cover.startsWith('http') ? cover : `${process.env.REACT_APP_BACKEND_URL}${cover}`} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-stone-300">
                                        <ImageIcon size={36} />
                                    </div>
                                )}
                                <span className={`absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${it.pubblicato ? "bg-emerald-500 text-white" : "bg-stone-300 text-stone-700"}`}>
                                    {it.pubblicato ? "Pubblicato" : "Bozza"}
                                </span>
                                {it.classificazione?.tipologia && (
                                    <span className="absolute top-3 left-3 bg-blue-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                                        {it.classificazione.tipologia}
                                    </span>
                                )}
                            </div>
                            <div className="p-4">
                                <div className="font-serif text-lg font-semibold text-stone-900 leading-tight line-clamp-2 mb-1">
                                    {it.anagrafica?.denominazione}
                                </div>
                                <div className="text-xs text-stone-500 flex items-center gap-1"><MapPin size={12} /> {it.anagrafica?.via_piazza}, {it.anagrafica?.comune}</div>
                                <div className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mt-1">{it.codice}</div>
                                {(it.dimensioni?.mq_calpestabili || it.dimensioni?.vani) && (
                                    <div className="text-xs text-stone-600 mt-1">{it.dimensioni?.mq_calpestabili || 0} mq · {it.dimensioni?.vani || 0} vani</div>
                                )}
                                <div className="mt-4 flex gap-2">
                                    <button onClick={() => setEditor({ open: true, item: it })} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm font-semibold" data-testid={`adm-edit-${it.codice}`}>
                                        <Edit size={13} /> Modifica
                                    </button>
                                    <a
                                        href={`${process.env.REACT_APP_BACKEND_URL}/api/immobili/${it.id}/pdf`}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Esporta scheda PDF"
                                        className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center justify-center"
                                        data-testid={`adm-pdf-${it.codice}`}
                                    >
                                        <Download size={14} />
                                    </a>
                                    <button onClick={() => del(it.id)} title="Elimina" className="w-9 h-9 rounded-full bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center" data-testid={`adm-del-${it.codice}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {!busy && filtered.length === 0 && (
                <div className="text-center py-16 text-stone-500 bg-white rounded-2xl border border-stone-200" data-testid="adm-imm-empty">
                    Nessun immobile ancora caricato. <br />
                    <button onClick={() => setEditor({ open: true, item: null })} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700">
                        <Plus size={14} /> Aggiungi il primo
                    </button>
                </div>
            )}

            <ImmobileEditor
                open={editor.open}
                immobile={editor.item}
                onClose={() => setEditor({ open: false, item: null })}
                onSaved={load}
            />
        </div>
    );
};

// ================= EVENTI PANEL =================
const emptyEvent = { title: "", category: "notizia", date_label: "", excerpt: "", body: "", image: "", published: true, order: 0 };

const EventiPanel = () => {
    const [items, setItems] = useState([]);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(true);

    const load = async () => {
        setBusy(true);
        const { data } = await adminAxios().get("/admin/events");
        setItems(data);
        setBusy(false);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const payload = { ...edit };
        if (payload.id) await adminAxios().put(`/admin/events/${payload.id}`, payload);
        else await adminAxios().post("/admin/events", payload);
        setEdit(null); load();
    };
    const del = async (id) => {
        if (!confirm("Confermi eliminazione evento?")) return;
        await adminAxios().delete(`/admin/events/${id}`); load();
    };

    return (
        <div data-testid="admin-eventi-panel">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-2xl font-semibold text-stone-900">Gestione Eventi & Notizie</h2>
                <button onClick={() => setEdit({ ...emptyEvent })} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm" data-testid="ev-new">
                    <Plus size={14} /> Nuovo Evento
                </button>
            </div>
            {busy && <div className="text-center py-8"><Loader2 size={20} className="animate-spin mx-auto text-blue-600" /></div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((e) => (
                    <div key={e.id} className="bg-white border border-stone-200 rounded-2xl p-4 flex gap-4" data-testid={`ev-item-${e.id}`}>
                        {e.image && <img src={e.image.startsWith('http') ? e.image : `${process.env.REACT_APP_BACKEND_URL}${e.image}`} alt="" className="w-24 h-24 rounded-xl object-cover" />}
                        <div className="flex-1">
                            <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">{e.category} · {e.date_label}</div>
                            <div className="font-serif text-lg font-semibold text-stone-900 leading-tight line-clamp-1">{e.title}</div>
                            <div className="text-xs text-stone-500 line-clamp-2 mt-1">{e.excerpt}</div>
                            <div className="mt-3 flex items-center gap-2">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${e.published ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-700"}`}>
                                    {e.published ? "Pubblicato" : "Bozza"}
                                </span>
                                <button onClick={() => setEdit(e)} className="ml-auto px-3 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-semibold" data-testid={`ev-edit-${e.id}`}><Edit size={11} className="inline mr-1" /> Modifica</button>
                                <button onClick={() => del(e.id)} className="px-2 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100" data-testid={`ev-del-${e.id}`}><Trash2 size={12} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {!busy && items.length === 0 && <div className="text-center py-12 text-stone-500 bg-white rounded-2xl border">Nessun evento.</div>}

            {edit && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ev-editor">
                    <div className="bg-white w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl">
                        <div className="px-6 py-4 bg-blue-600 text-white flex justify-between items-center">
                            <div className="font-serif text-xl font-semibold">{edit.id ? "Modifica" : "Nuovo"} evento</div>
                            <button onClick={() => setEdit(null)} className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center" data-testid="ev-close"><ImageIcon size={0} />×</button>
                        </div>
                        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                            <Input label="Titolo *" value={edit.title} onChange={(v) => setEdit({ ...edit, title: v })} testid="ev-title" />
                            <div className="grid grid-cols-2 gap-4">
                                <Select label="Categoria" value={edit.category} onChange={(v) => setEdit({ ...edit, category: v })} options={["notizia", "tradizione", "musica", "cultura", "sport", "gastronomia"]} testid="ev-cat" />
                                <Input label="Data (label)" value={edit.date_label} onChange={(v) => setEdit({ ...edit, date_label: v })} testid="ev-date" placeholder="14 Settembre 2025" />
                            </div>
                            <Textarea label="Anteprima" value={edit.excerpt} onChange={(v) => setEdit({ ...edit, excerpt: v })} rows={2} testid="ev-excerpt" />
                            <Textarea label="Testo completo" value={edit.body} onChange={(v) => setEdit({ ...edit, body: v })} rows={5} testid="ev-body" />
                            <div className="flex items-center justify-between">
                                <Input label="URL Immagine" value={edit.image} onChange={(v) => setEdit({ ...edit, image: v })} testid="ev-image" />
                                <div className="pt-6"><FileUploader accept="image/*" onUploaded={(u) => setEdit({ ...edit, image: u.url })} testid="ev-image-upload" /></div>
                            </div>
                            <div className="flex items-center justify-between">
                                <Toggle label="Pubblicato" value={edit.published} onChange={(v) => setEdit({ ...edit, published: v })} testid="ev-pub" />
                                <Input label="Ordine" type="number" value={edit.order} onChange={(v) => setEdit({ ...edit, order: v })} testid="ev-order" />
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-stone-50 border-t flex justify-end gap-3">
                            <button onClick={() => setEdit(null)} className="px-5 py-2 rounded-full border border-stone-300 text-stone-700 hover:bg-white text-sm font-semibold" data-testid="ev-cancel">Annulla</button>
                            <button onClick={save} className="btn-primary px-5 py-2 rounded-full text-sm font-semibold" data-testid="ev-save">Salva</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ================= ATTRAZIONI PANEL =================
const emptyAttr = { name: "", category: "attrazione", type_label: "", description: "", image: "", map_url: "", published: true, order: 0 };

const AttrazioniPanel = () => {
    const [items, setItems] = useState([]);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(true);

    const load = async () => {
        setBusy(true);
        const { data } = await adminAxios().get("/admin/attractions");
        setItems(data);
        setBusy(false);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const payload = { ...edit };
        if (payload.id) await adminAxios().put(`/admin/attractions/${payload.id}`, payload);
        else await adminAxios().post("/admin/attractions", payload);
        setEdit(null); load();
    };
    const del = async (id) => {
        if (!confirm("Confermi eliminazione attrazione?")) return;
        await adminAxios().delete(`/admin/attractions/${id}`); load();
    };

    return (
        <div data-testid="admin-attrazioni-panel">
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-2xl font-semibold text-stone-900">Gestione Attrattori POI</h2>
                <button onClick={() => setEdit({ ...emptyAttr })} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm" data-testid="at-new">
                    <Plus size={14} /> Nuova Attrazione
                </button>
            </div>
            {busy && <div className="text-center py-8"><Loader2 size={20} className="animate-spin mx-auto text-blue-600" /></div>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((a) => (
                    <div key={a.id} className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid={`at-item-${a.id}`}>
                        {a.image && <img src={a.image.startsWith('http') ? a.image : `${process.env.REACT_APP_BACKEND_URL}${a.image}`} alt="" className="w-full h-32 object-cover" />}
                        <div className="p-4">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">{a.category} · {a.type_label}</div>
                            <div className="font-serif text-lg font-semibold text-stone-900 leading-tight line-clamp-1">{a.name}</div>
                            <div className="text-xs text-stone-500 line-clamp-2 mt-1">{a.description}</div>
                            <div className="mt-3 flex items-center gap-2">
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${a.published ? "bg-emerald-100 text-emerald-800" : "bg-stone-200 text-stone-700"}`}>
                                    {a.published ? "Pubblicato" : "Bozza"}
                                </span>
                                <button onClick={() => setEdit(a)} className="ml-auto px-3 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 text-xs font-semibold" data-testid={`at-edit-${a.id}`}><Edit size={11} className="inline mr-1" /> Modifica</button>
                                <button onClick={() => del(a.id)} className="px-2 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100" data-testid={`at-del-${a.id}`}><Trash2 size={12} /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {!busy && items.length === 0 && <div className="text-center py-12 text-stone-500 bg-white rounded-2xl border">Nessuna attrazione.</div>}

            {edit && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="at-editor">
                    <div className="bg-white w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl">
                        <div className="px-6 py-4 bg-blue-600 text-white flex justify-between items-center">
                            <div className="font-serif text-xl font-semibold">{edit.id ? "Modifica" : "Nuova"} attrazione</div>
                            <button onClick={() => setEdit(null)} className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center text-xl" data-testid="at-close">×</button>
                        </div>
                        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                            <Input label="Nome *" value={edit.name} onChange={(v) => setEdit({ ...edit, name: v })} testid="at-name" />
                            <div className="grid grid-cols-2 gap-4">
                                <Select label="Categoria" value={edit.category} onChange={(v) => setEdit({ ...edit, category: v })} options={["attrazione", "natura", "mangiare", "dormire", "itinerario"]} testid="at-cat" />
                                <Input label="Type Label" value={edit.type_label} onChange={(v) => setEdit({ ...edit, type_label: v })} testid="at-type" placeholder="es. Chiesa, B&B, Ristorante" />
                            </div>
                            <Textarea label="Descrizione" value={edit.description} onChange={(v) => setEdit({ ...edit, description: v })} rows={4} testid="at-desc" />
                            <div className="flex items-end justify-between gap-3">
                                <div className="flex-1"><Input label="URL Immagine" value={edit.image} onChange={(v) => setEdit({ ...edit, image: v })} testid="at-image" /></div>
                                <FileUploader accept="image/*" onUploaded={(u) => setEdit({ ...edit, image: u.url })} testid="at-image-upload" />
                            </div>
                            <Input label="URL Mappa" value={edit.map_url} onChange={(v) => setEdit({ ...edit, map_url: v })} testid="at-map" placeholder="https://maps.google.com/?q=..." />
                            <div className="flex items-center justify-between">
                                <Toggle label="Pubblicato" value={edit.published} onChange={(v) => setEdit({ ...edit, published: v })} testid="at-pub" />
                                <Input label="Ordine" type="number" value={edit.order} onChange={(v) => setEdit({ ...edit, order: v })} testid="at-order" />
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-stone-50 border-t flex justify-end gap-3">
                            <button onClick={() => setEdit(null)} className="px-5 py-2 rounded-full border border-stone-300 text-stone-700 hover:bg-white text-sm font-semibold" data-testid="at-cancel">Annulla</button>
                            <button onClick={save} className="btn-primary px-5 py-2 rounded-full text-sm font-semibold" data-testid="at-save">Salva</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
